/**
 * Kopfzeile der Verlaufskarte (Board „Verlauf“): Ansichten Übertragungen · Entscheidungen · Gültige
 * Werte, ein Apartmentfeld, „Filter · n“ und „Als ZIP exportieren“ (am Handy „ZIP“) mit Hinweis.
 */
import type { ReactNode } from 'react';
import type { DynamicPricingHistoryView } from '@/shared/api';
import { Button } from '@/shared/components';
import { cn } from '@/shared/lib/cn';

const VIEWS: [DynamicPricingHistoryView, string][] = [
  ['publications', 'Übertragungen'],
  ['decisions', 'Entscheidungen'],
  ['exposures', 'Gültige Werte'],
];

interface Props {
  view: DynamicPricingHistoryView;
  mobile: boolean;
  apartments: { id: string; name: string }[];
  apartment?: string;
  filter: ReactNode;
  exporting: boolean;
  exportDisabled: boolean;
  onView: (view: DynamicPricingHistoryView) => void;
  onApartment: (id: string | undefined) => void;
  onExport: () => void;
}

export function HistoryToolbar(props: Props) {
  const { mobile } = props;
  return (
    <div
      className={cn(
        'border-hairline flex flex-col gap-2.5 border-b p-3 md:flex-row md:flex-wrap md:items-center md:gap-2 md:px-[18px] md:py-3.5',
      )}
    >
      <div
        role="tablist"
        aria-label="Ansicht"
        className="bg-card-alt rounded-pill grid grid-cols-3 gap-0.5 p-1 md:flex"
        onKeyDown={(event) => {
          // Reiter wie üblich: Pfeile, Pos1 und Ende wechseln die Ansicht.
          // Alt+Pfeil bleibt „Zurück“ des Browsers.
          if (event.altKey || event.ctrlKey || event.metaKey) return;
          const index = VIEWS.findIndex(([value]) => value === props.view);
          const step = { ArrowRight: 1, ArrowLeft: -1 }[event.key];
          const next =
            event.key === 'Home'
              ? 0
              : event.key === 'End'
                ? VIEWS.length - 1
                : step === undefined
                  ? -1
                  : (index + step + VIEWS.length) % VIEWS.length;
          if (next < 0) return;
          event.preventDefault();
          const [value] = VIEWS[next]!;
          props.onView(value);
          event.currentTarget.querySelector<HTMLElement>(`#history-tab-${value}`)?.focus();
        }}
      >
        {VIEWS.map(([value, label]) => {
          const on = value === props.view;
          return (
            <button
              key={value}
              type="button"
              role="tab"
              id={`history-tab-${value}`}
              aria-selected={on}
              tabIndex={on ? 0 : -1}
              aria-controls="dynamic-pricing-history-panel"
              onClick={() => props.onView(value)}
              className={cn(
                'rounded-pill min-h-11 truncate px-2 text-[13px] md:min-h-[34px] md:px-3',
                on ? 'text-ink bg-white font-bold shadow-sm' : 'text-ink-soft font-semibold',
              )}
            >
              {label}
            </button>
          );
        })}
      </div>
      <div className="grid grid-cols-[minmax(0,1fr)_auto_auto] gap-1.5 md:contents">
        <select
          aria-label="Apartment"
          value={props.apartment ?? ''}
          onChange={(event) => props.onApartment(event.target.value || undefined)}
          className="border-line rounded-pill text-ink h-11 min-w-0 truncate border bg-white px-3.5 text-[13px] font-semibold md:h-10 md:max-w-[16rem]"
        >
          <option value="">Alle Apartments</option>
          {props.apartments.map((item) => (
            <option key={item.id} value={item.id}>
              {item.name}
            </option>
          ))}
        </select>
        {props.filter}
        <span className="flex items-center gap-2 md:ml-auto">
          <span className="text-ink-soft hidden text-xs md:inline">
            Enthält alle drei Ansichten
          </span>
          <Button
            variant="secondary"
            aria-label={
              mobile && !props.exporting
                ? 'Als ZIP exportieren – enthält alle drei Ansichten'
                : undefined
            }
            title="Das ZIP enthält alle drei Ansichten mit dem aktuellen Filter."
            className="border-ink min-h-11 border-[1.5px] bg-white md:min-h-10"
            loading={props.exporting}
            disabled={props.exportDisabled}
            onClick={props.onExport}
          >
            {props.exporting ? 'Export läuft …' : mobile ? 'ZIP' : 'Als ZIP exportieren'}
          </Button>
        </span>
      </div>
    </div>
  );
}
